import { create } from "zustand";
import { keyName } from "./push";

/**
 * Свои сочетания — «Пользовательские горячие клавиши» Discord: микрофон и
 * звук одним сочетанием, в том числе поверх игр (в оболочке). Рация сюда не
 * входит: её клавиша — в настройке голоса (`pushKey`), раздел только
 * показывает её первой строкой.
 *
 * Живут на устройстве, как в Discord: клавиатуры и мыши у компьютеров разные.
 */
export type KeybindAction = "mute" | "deafen";

export interface Keybind {
  id: string;
  action: KeybindAction;
  /** `KeyboardEvent.code` и «Mouse3–5»; модификаторы — первыми. */
  keys: string[];
}

const KEY = "airel.keybinds";

function read(): Keybind[] {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    if (!Array.isArray(saved)) return [];
    return saved.filter(
      (bind): bind is Keybind =>
        !!bind &&
        typeof bind.id === "string" &&
        (bind.action === "mute" || bind.action === "deafen") &&
        Array.isArray(bind.keys) &&
        bind.keys.every((code: unknown) => typeof code === "string"),
    );
  } catch {
    return [];
  }
}

const useStore = create<Keybind[]>()(() => read());

export function keybinds(): Keybind[] {
  return useStore.getState();
}

export function useKeybinds(): Keybind[] {
  return useStore();
}

export function setKeybinds(next: Keybind[]): void {
  useStore.setState(next, true);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    // Хранилище закрыто — сочетания доживут до перезапуска.
  }
}

/** Модификаторы и как их зовут в сочетании: сторона там неважна. */
const MODIFIERS: Record<string, string> = {
  ControlLeft: "Ctrl",
  ControlRight: "Ctrl",
  ShiftLeft: "Shift",
  ShiftRight: "Shift",
  AltLeft: "Alt",
  AltRight: "Alt",
  MetaLeft: "Win",
  MetaRight: "Win",
};

export function isModifier(code: string): boolean {
  return code in MODIFIERS;
}

/**
 * Сочетание человеку: «Ctrl + Shift + M», «Кнопка мыши 4». Одиночный
 * модификатор — со стороной («Правый Ctrl»): рацией он ровно тот, что задан.
 */
export function comboName(keys: string[]): string {
  if (keys.length === 1) return keyName(keys[0]);
  return keys.map((code) => MODIFIERS[code] ?? keyName(code)).join(" + ");
}

/**
 * Нажато ли сочетание этим событием окна: последняя клавиша — та, что
 * нажали, модификаторы — ровно те, что зажаты. Сочетание из одних
 * модификаторов окно не ловит — его слышит только оболочка.
 */
export function pressedBy(keys: string[], code: string, event: KeyboardEvent | MouseEvent): boolean {
  const main = keys.filter((key) => !isModifier(key));
  if (main.length !== 1 || main[0] !== code) return false;
  const wanted = new Set(keys.filter(isModifier).map((key) => MODIFIERS[key]));
  return (
    wanted.has("Ctrl") === event.ctrlKey &&
    wanted.has("Shift") === event.shiftKey &&
    wanted.has("Alt") === event.altKey &&
    wanted.has("Win") === event.metaKey
  );
}
